import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { Login } from "@/auth/Login";
import { TotpGate } from "@/auth/Totp";
import { CommandPalette } from "@/components/CommandPalette";
import { Shell } from "@/components/Shell";
import { ShortcutsLayer } from "@/components/Shortcuts";
import { ToastProvider } from "@/components/Toast";
import { Approvals } from "@/pages/Approvals";
import { AuditLog } from "@/pages/AuditLog";
import { Costs } from "@/pages/Costs";
import { Dashboard } from "@/pages/Dashboard";
import { Import } from "@/pages/Import";
import { Keys } from "@/pages/Keys";
import { Onboard } from "@/pages/Onboard";
import { ProjectDetail } from "@/pages/ProjectDetail";
import { ProjectsMonitor } from "@/pages/ProjectsMonitor";
import { ProxyKeyDetail } from "@/pages/ProxyKeyDetail";
import { ProxyKeys } from "@/pages/ProxyKeys";
import { RealKeyDetail } from "@/pages/RealKeyDetail";
import { RealKeys } from "@/pages/RealKeys";
import { Settings } from "@/pages/Settings";
import { Users } from "@/pages/Users";

function AuthenticatedRoutes() {
  const { me, loading, pendingTotp } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }
  // If we just logged in but /me hasn't populated yet (transient cookie failure
  // or slow network), `pendingTotp` from the login response is still authoritative
  // — send the user to the TOTP gate, not back to /login. Without this branch
  // a transient /me 401 right after login would discard the freshly-set cookie
  // state and force the user to re-login (audit finding #1, 2026-04-26).
  if (!me && pendingTotp) return <Navigate to="/totp" replace />;
  if (!me) return <Navigate to="/login" replace />;
  // Force TOTP when: password stage completed but TOTP not yet, OR user hasn't enrolled yet.
  if (pendingTotp || !me.totp_ok || !me.totp_enrolled) return <Navigate to="/totp" replace />;
  return (
    <>
      <ShortcutsLayer />
      <CommandPalette />
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Dashboard />} />
          <Route path="/projects" element={<ProjectsMonitor />} />
          <Route path="/projects/:name" element={<ProjectDetail />} />
          <Route path="/real-keys" element={<RealKeys />} />
          <Route path="/real-keys/:id" element={<RealKeyDetail />} />
          <Route path="/proxy-keys" element={<ProxyKeys />} />
          <Route path="/proxy-keys/:id" element={<ProxyKeyDetail />} />
          <Route path="/keys" element={<Keys />} />
          <Route path="/import" element={<Import />} />
          <Route path="/onboard" element={<Onboard />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/costs" element={<Costs />} />
          <Route path="/users" element={<Users />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/totp" element={<TotpGate />} />
          <Route path="/*" element={<AuthenticatedRoutes />} />
        </Routes>
      </AuthProvider>
    </ToastProvider>
  );
}
